import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formsAPI } from '../api/client';

export default function CandidateForm() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    formsAPI.getPublicForm(token)
      .then((res) => {
        setForm(res.data);
        const defaults = {};
        res.data.questions.forEach((q) => {
          defaults[q.id] = q.question_type === 'checkbox' ? [] : '';
        });
        setAnswers(defaults);
      })
      .catch((err) => setError(err.response?.data?.detail || 'Form not found or link has expired'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleCheckbox = (questionId, option, checked) => {
    setAnswers((prev) => {
      const current = prev[questionId] || [];
      return {
        ...prev,
        [questionId]: checked ? [...current, option] : current.filter((v) => v !== option),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required
    for (const q of form.questions) {
      if (q.required) {
        const val = answers[q.id];
        if (!val || (Array.isArray(val) && val.length === 0) || String(val).trim() === '') {
          alert(`Please answer: "${q.question_text}"`);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      await formsAPI.submitForm(token, { responses: answers });
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8">
        <p className="text-5xl mb-4">❌</p>
        <h1 className="text-xl font-bold text-gray-800 mb-2">Form Unavailable</h1>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-teal-50">
      <div className="text-center p-8 max-w-md">
        <p className="text-6xl mb-4">✅</p>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Form Submitted!</h1>
        <p className="text-gray-600 mb-4">
          Thank you, <strong>{form.candidate_name}</strong>! Your responses have been received.
        </p>
        <p className="text-sm text-gray-400">
          You will receive a confirmation email shortly. We'll be in touch with next steps.
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-50 py-10 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">AI</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Candidate Information Form</h1>
          <p className="text-gray-500 mt-1">
            Hi <strong>{form.candidate_name}</strong> — applying for{' '}
            <strong>{form.position_title}</strong>
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {form.questions.map((q, idx) => (
              <div key={q.id}>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  {idx + 1}. {q.question_text}
                  {q.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <QuestionInput
                  question={q}
                  value={answers[q.id]}
                  onChange={(val) => handleChange(q.id, val)}
                  onCheckbox={(opt, checked) => handleCheckbox(q.id, opt, checked)}
                />
              </div>
            ))}

            {form.questions.length === 0 && (
              <p className="text-center text-gray-400 py-4">No questions configured.</p>
            )}

            <button type="submit" disabled={submitting}
              className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-60 transition-colors">
              {submitting ? 'Submitting...' : 'Submit Form'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          This form is confidential and for recruitment purposes only.
        </p>
      </div>
    </div>
  );
}

function QuestionInput({ question, value, onChange, onCheckbox }) {
  const cls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300";

  switch (question.question_type) {
    case 'textarea':
      return <textarea rows={4} className={cls} value={value} placeholder={question.placeholder}
        onChange={(e) => onChange(e.target.value)} />;
    case 'select':
      return (
        <select className={cls} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select an option...</option>
          {(question.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    case 'radio':
      return (
        <div className="space-y-2">
          {(question.options || []).map((opt) => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name={`q_${question.id}`} value={opt} checked={value === opt}
                onChange={() => onChange(opt)} className="text-primary-600" />
              <span className="text-sm text-gray-700">{opt}</span>
            </label>
          ))}
        </div>
      );
    case 'checkbox':
      return (
        <div className="space-y-2">
          {(question.options || []).map((opt) => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={(value || []).includes(opt)}
                onChange={(e) => onCheckbox(opt, e.target.checked)} className="text-primary-600 rounded" />
              <span className="text-sm text-gray-700">{opt}</span>
            </label>
          ))}
        </div>
      );
    default:
      return <input type={question.question_type} className={cls} value={value}
        placeholder={question.placeholder} onChange={(e) => onChange(e.target.value)} />;
  }
}
